import { ArrowRight, MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import { SDL_LOGO, LDA_LOGO } from "@/lib/assets";

export default function Footer({ onBook }) {
  return (
    <footer
      data-testid="footer"
      style={{ background: '#16110F', borderTop: '1px solid #3A2E28' }}
    >
      {/* CTA Banner */}
      <div
        className="py-16 px-4 sm:px-8"
        style={{ borderBottom: '1px solid #3A2E28' }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="font-outfit text-xs tracking-[0.25em] uppercase mb-2" style={{ color: '#D4AF37' }}>
              Dr. Shihab Romeed
            </p>
            <h2
              className="font-cormorant font-light"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: '#FDF8ED' }}
            >
              Ready to take the next step?
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="footer-patient-book-btn"
              onClick={() => onBook('patient')}
              className="flex items-center gap-2 font-outfit text-sm font-medium px-6 py-3 transition-all duration-200 hover:opacity-90 active:scale-95"
              style={{ background: '#D4AF37', color: '#16110F' }}
            >
              Patient Consultation
              <ArrowRight size={15} />
            </button>
            <button
              data-testid="footer-academy-book-btn"
              onClick={() => onBook('professional')}
              className="flex items-center gap-2 font-outfit text-sm font-medium px-6 py-3 border transition-all duration-200 hover:border-[#1CB0C6] hover:text-[#1CB0C6] active:scale-95"
              style={{ border: '1px solid #3A2E28', color: '#A89886' }}
            >
              Academy Enrolment
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="py-16 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Col 1: About */}
          <div className="lg:col-span-1">
            <h4 className="font-cormorant text-xl font-light mb-4" style={{ color: '#FDF8ED' }}>
              Dr. Shihab Romeed
            </h4>
            <p className="font-outfit text-sm leading-relaxed mb-4" style={{ color: '#A89886' }}>
              Consultant in Restorative Dentistry. Clinical Director at Specialist Dental London. Founder, London Dental Academy.
            </p>
            <p className="font-outfit text-xs" style={{ color: '#A89886' }}>
              GDC No. — Specialist Register:<br />
              Restorative Dentistry, Prosthodontics,<br />
              Periodontics, Endodontics
            </p>
          </div>

          {/* Col 2: Practice */}
          <div>
            <div className="w-36 h-10 overflow-hidden mb-4" style={{ background: '#16110F' }}>
              <img src={SDL_LOGO} alt="Specialist Dental London" className="w-full h-full object-contain logo-screen" />
            </div>
            <ul className="space-y-2 mb-5">
              {['Dental Implants', 'Bone Grafting', 'Periodontics', 'Endodontics', 'Prosthodontics', 'Full-Arch Rehab'].map(s => (
                <li key={s} className="font-outfit text-sm" style={{ color: '#A89886' }}>
                  {s}
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-2">
              <MapPin size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" style={{ color: '#D4AF37' }} />
              <span className="font-outfit text-xs leading-relaxed" style={{ color: '#78716C' }}>
                London, United Kingdom
              </span>
            </div>
          </div>

          {/* Col 3: Academy */}
          <div>
            <div className="w-36 h-10 overflow-hidden mb-4" style={{ background: '#082025' }}>
              <img src={LDA_LOGO} alt="London Dental Academy" className="w-full h-full object-contain logo-screen" />
            </div>
            <ul className="space-y-2 mb-5">
              {['Advanced Endodontic Practice', 'Dental Implants & Prosthodontics', 'Restoring Implants', 'Aesthetic Zone Placement', 'Posterior & Sinus Cases'].map(c => (
                <li key={c} className="font-outfit text-sm" style={{ color: '#A89886' }}>
                  {c}
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-2">
              <MapPin size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" style={{ color: '#1CB0C6' }} />
              <span className="font-outfit text-xs leading-relaxed" style={{ color: '#78716C' }}>
                London, United Kingdom
              </span>
            </div>
          </div>

          {/* Col 4: Contact */}
          <div>
            <h4 className="font-outfit text-xs tracking-[0.25em] uppercase mb-5" style={{ color: '#A89886' }}>
              Contact
            </h4>
            <div className="space-y-4 mb-6">
              <a
                href="tel:+442000000000"
                data-testid="footer-phone-link"
                className="flex items-center gap-3 font-outfit text-sm transition-colors duration-200 hover:text-white group"
                style={{ color: '#A89886' }}
              >
                <Phone size={14} strokeWidth={1.5} style={{ color: '#D4AF37' }} />
                +44 (0)20 0000 0000
              </a>
              <a
                href="mailto:info@specialistdentallondon.co.uk"
                data-testid="footer-email-link"
                className="flex items-center gap-3 font-outfit text-sm transition-colors duration-200 hover:text-white group break-all"
                style={{ color: '#A89886' }}
              >
                <Mail size={14} strokeWidth={1.5} style={{ color: '#D4AF37' }} />
                info@specialistdentallondon.co.uk
              </a>
            </div>

            <div className="space-y-2">
              <a
                href="#"
                data-testid="footer-sdl-external"
                className="flex items-center gap-2 font-outfit text-xs transition-colors duration-200 hover:text-[#D4AF37]"
                style={{ color: '#78716C' }}
              >
                specialistdentallondon.co.uk <ExternalLink size={10} />
              </a>
              <a
                href="#"
                data-testid="footer-lda-external"
                className="flex items-center gap-2 font-outfit text-xs transition-colors duration-200 hover:text-[#1CB0C6]"
                style={{ color: '#78716C' }}
              >
                londondentalaacademy.co.uk <ExternalLink size={10} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="py-5 px-4 sm:px-8"
        style={{ borderTop: '1px solid #3A2E28' }}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-outfit text-xs" style={{ color: '#78716C' }}>
            &copy; {new Date().getFullYear()} Dr. Shihab Romeed. All rights reserved.
          </p>
          <p className="font-outfit text-xs" style={{ color: '#78716C' }}>
            GDC Registered Specialist — Restorative Dentistry
          </p>
        </div>
      </div>
    </footer>
  );
}
